import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import MainLayout from "@/layouts/MainLayout/MainLayout.tsx";
import AuthLayout from "@/layouts/AuthLayout/AuthLayout.tsx";
import Login from "@/pages/auth/Login/Login.tsx";
import ForgotPassword from "@/pages/auth/ForgotPassword/ForgotPassword.tsx";
import HealthCheck from "@/HealthCheck.tsx";

import "@/App.css";
import {
  ASSIGNED_URL,
  COMPLETED_URL,
  ASSISTANT_URL,
  DASHBOARD_URL,
  FORGOT_PASSWORD_URL,
  HEALTH_CHECK_URL,
  INVOICED_URL,
  LOGIN_URL,
  PENDING_URL,
  INVOICE_PDF_URL,
  PROJECT_INVOICE_URL,
  PROJECT_INVOICES_URL,
  PROJECT_ASSISTANT_URL,
  PROJECT_DASHBOARD_URL,
  PROJECT_URL,
  PROJECTS_URL,
  SUPERVISOR_ASSIGNED_URL,
  USER_ASSIGNED_URL,
  USER_PROJECT_ASSIGNED_URL,
  USER_PROJECT_DELIVERY_URL,
  PROJECT_PENDING_URL,
  PROJECT_PENDING_BID_PACKAGE_URL,
  PROJECT_COMPLETED_URL,
  MEDIA_SHUTTLE_URL,
  NEW_TASK_URL,
  ASSET_FILES_URL,
  PROJECT_ASSIGNED_URL,
  CUSTOM_BID,
  FLASH_BID,
  EDIT_SOURCE_MEDIA_URL,
  EDIT_PROJECT_REFERENCES_URL,
  EDIT_PROJECT_DEADLINE_URL,
  EDIT_PROJECT_SPECS_URL,
  EDIT_PROJECT_CORRECTIONS_URL,
  ACTIONS_URL,
  FINANCIAL_DASHBOARD_URL,
  PROJECT_COMPLETED_DETAIL_URL,
  PROJECT_ASSIGNED_DETAIL_URL,
  PROJECT_DELIVERY_CHECK_URL,
  SIGNUP_URL,
  COMPANY_CHAT_URL,
  NEW_SUB_TASK_URL,
  MANAGE_EC2_INSTANCES,
} from "@/helpers/url.ts";
import React from "react";
import { useUserContext } from "@/contexts/UserContextProvider.tsx";
import NameValidator from "./components/common/NameValidation/NameValidator";
import UserDeliverTask from "./pages/Assigned/UserDelivery/UserDeliver";
import HttpUpload from "./components/common/HttpUpload/HttpUpload";
import SignUpPage from "./pages/auth/Signup/SignUpPage";
import SubTaskFlowContainer from "./components/common/CreateNewSubTask/CreateNewSubTask";

const Home = React.lazy(() => import("@/pages/Home/Home.tsx"));
const ArtistHome = React.lazy(() => import("@/pages/Home/ArtistHome.tsx"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard/Dashboard.tsx"));
const Assistant = React.lazy(() => import("@/pages/Assistant/Assistant.tsx"));
const Pending = React.lazy(() => import("@/pages/Pending/Pending.tsx"));
const Assigned = React.lazy(() => import("@/pages/Assigned/Assigned.tsx"));
const Invoiced = React.lazy(() => import("@/pages/Invoiced/Invoiced.tsx"));
const Invoice = React.lazy(() => import("@/pages/Invoice/Invoice.tsx"));
const InvoicePdf = React.lazy(
  () => import("@/pages/InvoicePdf/InvoicePdf.tsx"),
);
const Completed = React.lazy(() => import("@/pages/Completed/Completed.tsx"));
const MediaShuttleWrapper = React.lazy(
  () => import("@/components/common/MediaShuttle/MediaShuttleWrapper.tsx"),
);
const NotFound = React.lazy(() => import("@/pages/NotFound/NotFound.tsx"));

const BidSelectionPage = React.lazy(
  () => import("@/pages/NewTaskPage/BidSelectionPage/SelectBidCreationMethod"),
);
const NewTaskFlowPage = React.lazy(
  () => import("@/pages/NewTaskPage/NewTaskPage"),
);
const FlashBidPage = React.lazy(
  () => import("@/pages/NewTaskPage/FlashBid/FlashbidWrapper"),
);

const AssetFiles = React.lazy(
  () => import("@/pages/AssetFiles/AssetFiles.tsx"),
);

const ClientUpdateSourceMedia = React.lazy(
  () =>
    import("@/components/common/UpdateMenu/ClientUpdateSourceMedia/ClientUpdateSourceMedia"),
);
const ClientUpdateProjectReferences = React.lazy(
  () =>
    import("@/components/common/UpdateMenu/ClientUpdateProjectReferences/ClientUpdateProjectReferences"),
);
const ClientUpdateProjectDeadline = React.lazy(
  () =>
    import("@/components/common/UpdateMenu/ClientUpdateProjectDeadline/ClientUpdateProjectDeadline"),
);
const ClientUpdateProjectSpecs = React.lazy(
  () =>
    import("@/components/common/UpdateMenu/ClientUpdateProjectSpecs/ClientUpdateProjectSpecs"),
);
const ClientUpdateProjectCorrections = React.lazy(
  () =>
    import("@/components/common/UpdateMenu/ClientUpdateProjectCorrections/ClientUpdateProjectCorrections"),
);
const Actions = React.lazy(() =>
  import("@/components/Actions/Actions.tsx").then((module) => {
    return { default: module.ActionRouter };
  }),
);
const FinancialDashboard = React.lazy(
  () => import("@/components/FinancialDashboard/FinancialDashboard"),
);
const UserAssigned = React.lazy(
  () => import("@/pages/UserAssigned/UserAssigned.tsx"),
);

const ManageEC2Instances = React.lazy(
  () => import("@/components/ManageEC2Instances/ManageEC2Instances.tsx"),
);

export default function App() {
  const { currentUser, isAuthenticated, assistantEnabled } = useUserContext();
  const location = useLocation();

  return (
    <Routes>
      <Route path={HEALTH_CHECK_URL} element={<HealthCheck />} />
      <Route element={<AuthLayout />}>
        <Route path={LOGIN_URL} element={<Login />} />
        <Route path={SIGNUP_URL} element={<SignUpPage />} />
        <Route path={FORGOT_PASSWORD_URL} element={<ForgotPassword />} />
      </Route>

      {!isAuthenticated || !currentUser ? (
        <Route
          path="*"
          element={
            <Navigate
              to={{
                pathname: LOGIN_URL,
                search: `?next=${location.pathname}`,
              }}
              state={{ showLoggedOutAlert: true, next: location.pathname }}
            />
          }
        />
      ) : currentUser.isSupervisor ? (
        <Route element={<MainLayout />}>
          <Route path={SUPERVISOR_ASSIGNED_URL} element={<NotFound />} />
        </Route>
      ) : currentUser.canBeAssignedTasks ? (
        <Route element={<MainLayout />}>
          <Route path={PROJECTS_URL} element={<ArtistHome />} />
          <Route path={MEDIA_SHUTTLE_URL} element={<MediaShuttleWrapper />} />
          <Route path={USER_ASSIGNED_URL} element={<UserAssigned />} />
          <Route path={USER_PROJECT_ASSIGNED_URL} element={<UserAssigned />} />
          <Route
            path={USER_PROJECT_DELIVERY_URL}
            element={
              <HttpUpload menuOpen={true}>
                {(props) => <UserDeliverTask uploadProps={props} />}
              </HttpUpload>
            }
          />
        </Route>
      ) : (
        <Route element={<MainLayout />}>
          <Route path={PROJECTS_URL} element={<Home />} />

          <Route path={PROJECT_URL} element={<Dashboard />} />
          <Route path={DASHBOARD_URL} element={<Dashboard />} />
          <Route path={PROJECT_DASHBOARD_URL} element={<Dashboard />} />
          <Route path={COMPANY_CHAT_URL} element={<Dashboard />} />

          {assistantEnabled && (
            <>
              <Route path={ASSISTANT_URL} element={<Assistant />} />
              <Route path={PROJECT_ASSISTANT_URL} element={<Assistant />} />
            </>
          )}

          <Route path={PENDING_URL} element={<Pending />} />
          <Route path={PROJECT_PENDING_URL} element={<Pending />} />
          <Route path={PROJECT_PENDING_BID_PACKAGE_URL} element={<Pending />} />

          <Route path={ASSIGNED_URL} element={<Assigned />} />
          <Route path={PROJECT_ASSIGNED_URL} element={<Assigned />} />
          <Route path={PROJECT_ASSIGNED_DETAIL_URL} element={<Assigned />} />
          <Route path={NEW_SUB_TASK_URL} element={<SubTaskFlowContainer />} />

          <Route path={INVOICED_URL} element={<Invoiced />} />
          <Route path={PROJECT_INVOICES_URL} element={<Invoiced />} />
          <Route path={PROJECT_INVOICE_URL} element={<Invoice />} />
          <Route path={COMPLETED_URL} element={<Completed />} />
          <Route path={PROJECT_COMPLETED_URL} element={<Completed />} />
          <Route path={PROJECT_COMPLETED_DETAIL_URL} element={<Completed />} />
          <Route path={MEDIA_SHUTTLE_URL} element={<MediaShuttleWrapper />} />

          <Route path={NEW_TASK_URL} element={<BidSelectionPage />} />
          <Route path={CUSTOM_BID} element={<NewTaskFlowPage />} />
          <Route path={FLASH_BID} element={<FlashBidPage />} />

          <Route path={ASSET_FILES_URL} element={<AssetFiles />} />

          {/* Update Routes */}
          <Route
            path={EDIT_SOURCE_MEDIA_URL}
            element={<ClientUpdateSourceMedia />}
          />
          <Route
            path={EDIT_PROJECT_REFERENCES_URL}
            element={<ClientUpdateProjectReferences />}
          />
          <Route
            path={EDIT_PROJECT_DEADLINE_URL}
            element={<ClientUpdateProjectDeadline />}
          />
          <Route
            path={EDIT_PROJECT_SPECS_URL}
            element={<ClientUpdateProjectSpecs />}
          />
          <Route
            path={EDIT_PROJECT_CORRECTIONS_URL}
            element={<ClientUpdateProjectCorrections />}
          />

          <Route path={ACTIONS_URL} element={<Actions />} />

          <Route
            path={PROJECT_DELIVERY_CHECK_URL}
            element={<NameValidator />}
          />

          <Route
            path={FINANCIAL_DASHBOARD_URL}
            element={<FinancialDashboard />}
          />

          <Route path={MANAGE_EC2_INSTANCES} element={<ManageEC2Instances />} />
        </Route>
      )}

      <Route path="*" element={<Navigate to={PROJECTS_URL} />} />
      <Route path={INVOICE_PDF_URL} element={<InvoicePdf />} />
    </Routes>
  );
}
